import type { ThreeEvent } from '@react-three/fiber'
import { useAppSelector } from 'common/hooks/redux'
import { ElementIdLabel } from 'features/Procedure/procedure-model/element-id-label'
import type { DrawingElementDescription } from 'features/Procedure/state/procedure.type'
import { formatDrawingNoteDisplay } from 'features/Procedure/utils/drawingDescription.utils'
import React, { useEffect, useMemo, useRef } from 'react'
import * as THREE from 'three'
import { Vector3 } from 'three'
import { materialCache } from './material-cache'
import { createXCurves } from './utils'

type Props = {
  center: Vector3
  size: number
  color: string
  lineWidth: number
  bodyMesh?: THREE.Object3D
  cameraPosition?: THREE.Vector3
  isSelected?: boolean
  isFromSelectedProcedure?: boolean
  onClick?: (event: ThreeEvent<MouseEvent>) => void
  description?: DrawingElementDescription
  isPreview?: boolean
  useDepthTest?: boolean
  inactiveOpacity?: number
}

export const XShape = React.memo(
  ({
    center,
    size,
    color,
    lineWidth,
    bodyMesh,
    cameraPosition,
    isSelected = false,
    isFromSelectedProcedure = false,
    onClick,
    description,
    isPreview = false,
    useDepthTest = false,
    inactiveOpacity = 0,
  }: Props) => {
    const descriptionText = useMemo(() => formatDrawingNoteDisplay(description), [description])

    const lineRef1 = useRef<THREE.Mesh>(null)
    const lineRef2 = useRef<THREE.Mesh>(null)
    const pickRef1 = useRef<THREE.Mesh>(null)
    const pickRef2 = useRef<THREE.Mesh>(null)
    const showElementIds = useAppSelector((state) => state.procedureReducer.showElementIds)
    const isPrinting = useAppSelector((state) => state.procedureReducer.isPrinting)

    useEffect(() => {
      if (size > 0) {
        const [curve1, curve2] = createXCurves(center, size, bodyMesh, cameraPosition)

        const createTube = (curve: THREE.Curve<Vector3>, pickWidth: number) =>
          new THREE.TubeGeometry(curve, 32, pickWidth / 100, 8, false)

        ;[lineRef1, lineRef2].forEach((ref, i) => {
          const curve = i === 0 ? curve1 : curve2
          if (ref.current) {
            if (ref.current.geometry) ref.current.geometry.dispose()
            ref.current.geometry = createTube(curve, lineWidth)
          }
        })
        ;[pickRef1, pickRef2].forEach((ref, i) => {
          const curve = i === 0 ? curve1 : curve2
          if (ref.current) {
            if (ref.current.geometry) ref.current.geometry.dispose()
            ref.current.geometry = createTube(curve, lineWidth * 4)
          }
        })
      }

      return () => {
        ;[lineRef1, lineRef2, pickRef1, pickRef2].forEach((ref) => {
          if (ref.current?.geometry) {
            ref.current.geometry.dispose()
          }
        })
      }
    }, [
      center.x,
      center.y,
      center.z,
      size,
      lineWidth,
      bodyMesh,
      cameraPosition?.x,
      cameraPosition?.y,
      cameraPosition?.z,
    ])

    const material = materialCache.getLineMaterial(
      color,
      isSelected,
      isFromSelectedProcedure,
      isPrinting,
      isPreview,
      useDepthTest,
      inactiveOpacity,
    )
    const pickMaterial = materialCache.getPickMaterial()

    useEffect(() => {
      ;[lineRef1, lineRef2].forEach((ref) => {
        if (ref.current) ref.current.material = material
      })
      ;[pickRef1, pickRef2].forEach((ref) => {
        if (ref.current) ref.current.material = pickMaterial
      })
    }, [
      material,
      pickMaterial,
      color,
      isSelected,
      isFromSelectedProcedure,
      isPrinting,
      isPreview,
      useDepthTest,
      inactiveOpacity,
    ])

    const handleClick = (event: ThreeEvent<PointerEvent>) => {
      if (onClick) {
        event.stopPropagation()
        onClick(event)
      }
    }

    if (size <= 0) return null

    return (
      <>
        <mesh ref={pickRef1} onClick={handleClick} />
        <mesh ref={lineRef1} onClick={handleClick} />
        <mesh ref={pickRef2} onClick={handleClick} />
        <mesh ref={lineRef2} onClick={handleClick} />
        {showElementIds && isFromSelectedProcedure && descriptionText && (
          <ElementIdLabel
            text={descriptionText}
            position={center}
            color={color}
            useDepthTest={false}
            onClick={onClick}
            offset={{
              x: Math.abs(center.x) > Math.abs(center.z) ? (center.x > 0 ? 1 : -1) : 0,
              y: 0.5,
              z: Math.abs(center.z) >= Math.abs(center.x) ? (center.z >= 0 ? 1 : -1) : 0,
            }}
          />
        )}
      </>
    )
  },
)
